/**
 * Matching scanned medical aid text (an admission sheet, a member card) to the scheme reference. Ported from
 * Connect's `scheme-match.helper.ts` and `SchemeResolverService` (tasks/scheme-linking-central.md).
 */
import type { SchemeEntry, SchemeOptionEntry } from './scheme-reference-loader';

const MAX_CANDIDATES = 12;

/** Uppercase, letters and digits only. */
export function normaliseSchemeName(s: string | null | undefined): string {
  return (s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Exact normalised match wins; otherwise the shortest scheme whose name contains the text or is contained by it. */
export function matchScheme(schemes: readonly SchemeEntry[], name: string | null | undefined): SchemeEntry | null {
  const q = normaliseSchemeName(name);
  if (!q) return null;
  const exact = schemes.find((s) => normaliseSchemeName(s.name) === q);
  if (exact) return exact;
  const candidates = schemes.filter((s) => {
    const n = normaliseSchemeName(s.name);
    return n.length > 0 && (n.includes(q) || q.includes(n));
  });
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => normaliseSchemeName(a.name).length - normaliseSchemeName(b.name).length)[0];
}

/** 0 = no match; higher = stronger (exact label/plan/option > containment > re-ordered words). */
function optionMatchRank(o: SchemeOptionEntry, q: string): number {
  const label = normaliseSchemeName(o.label);
  const plan = normaliseSchemeName(o.planName);
  const opt = normaliseSchemeName(o.optionName);
  if (label === q || plan === q || opt === q) return 3;
  if (label && (label.includes(q) || q.includes(label))) return 2;
  // Re-ordered colloquial names, e.g. "Classic Saver" for the catalogue's "Saver - Classic".
  if ((opt && q.includes(opt)) || (plan && q.includes(plan))) return 1;
  return 0;
}

/** The scheme's options matching the plan text, best first. */
export function optionCandidates(scheme: SchemeEntry, planText: string | null | undefined): SchemeOptionEntry[] {
  const q = normaliseSchemeName(planText);
  if (!q) return [];
  return scheme.options
    .map((o) => ({ o, rank: optionMatchRank(o, q) }))
    .filter((x) => x.rank > 0)
    .sort((a, b) => b.rank - a.rank)
    .map((x) => x.o);
}

export function matchOption(scheme: SchemeEntry, planText: string | null | undefined): SchemeOptionEntry | null {
  return optionCandidates(scheme, planText)[0] ?? null;
}

export interface SchemeDisambiguationRequest {
  schemeText: string;
  planText: string;
  candidates: { id: string; schemeName: string; planName: string; optionName: string; label: string }[];
}

const optionId = (o: SchemeOptionEntry) => `${o.planCode}:${o.optionCode}`;

/**
 * The scheme and option the scanned text most likely means: a learned match first, or null when the scheme is not listed or no option
 * matches (the text is then kept as typed). Several matching options go to `disambiguate` (the AI), which must name
 * one it was offered; when it cannot, the best text match is used.
 */
export async function resolveScannedMedicalAid(
  schemes: readonly SchemeEntry[],
  fields: { medicalAid?: string | null; medicalAidPlan?: string | null },
  disambiguate: (req: SchemeDisambiguationRequest) => Promise<string | null>,
  learned: readonly LearnedMatch[] = [],
): Promise<{ scheme: SchemeEntry; option: SchemeOptionEntry } | null> {
  const known = learnedPick(schemes, learned, fields);
  if (known) return known;
  const scheme = matchScheme(schemes, fields.medicalAid);
  if (!scheme) return null;
  const candidates = optionCandidates(scheme, fields.medicalAidPlan).slice(0, MAX_CANDIDATES);
  if (!candidates.length) return null;
  if (candidates.length === 1) return { scheme, option: candidates[0] };
  try {
    const bestId = await disambiguate({
      schemeText: fields.medicalAid ?? '',
      planText: fields.medicalAidPlan ?? '',
      candidates: candidates.map((o) => ({
        id: optionId(o), schemeName: scheme.name, planName: o.planName ?? '', optionName: o.optionName ?? '', label: o.label ?? '',
      })),
    });
    return { scheme, option: candidates.find((o) => optionId(o) === bestId) ?? candidates[0] };
  } catch {
    return { scheme, option: candidates[0] };
  }
}

/** Typed text people linked to these codes often enough (Connect's `MedicalAidMappings`, sent with the reference). */
export interface LearnedMatch {
  scheme: string;
  plan: string | null;
  option: string | null;
  schemeCode: string;
  planCode: string;
  optionCode: string;
  matchCount: number;
}

const LEARNED_SIMILARITY = 0.9;

/** 0 to 1: one minus the edit distance over the longer length, ignoring case. As Connect's string-similarity.helper. */
export function stringSimilarity(a: string, b: string): number {
  const la = a.toLowerCase();
  const lb = b.toLowerCase();
  if (la === lb) return 1;
  const maxLen = Math.max(la.length, lb.length);
  if (maxLen === 0) return 1;
  let prev = Array.from({ length: lb.length + 1 }, (_, j) => j);
  for (let i = 1; i <= la.length; i++) {
    const row = [i];
    for (let j = 1; j <= lb.length; j++) row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (la[i - 1] === lb[j - 1] ? 0 : 1));
    prev = row;
  }
  return 1 - prev[lb.length] / maxLen;
}

/**
 * The learned match closest to the scanned text, as Connect's suggest endpoint scores it (90% similar or more, weighted
 * by how often it was confirmed), when the reference still has its scheme and option.
 */
export function learnedPick(
  schemes: readonly SchemeEntry[],
  learned: readonly LearnedMatch[],
  fields: { medicalAid?: string | null; medicalAidPlan?: string | null },
): { scheme: SchemeEntry; option: SchemeOptionEntry } | null {
  const schemeText = (fields.medicalAid ?? '').trim();
  const planText = (fields.medicalAidPlan ?? '').trim();
  if (!schemeText) return null;
  let best: { score: number; match: LearnedMatch } | null = null;
  for (const m of learned) {
    let similarity = stringSimilarity(schemeText, m.scheme);
    if (planText && m.plan) similarity = (similarity + stringSimilarity(planText, m.plan)) / 2;
    if (similarity < LEARNED_SIMILARITY) continue;
    const score = similarity * (1 + Math.log(1 + (m.matchCount || 1)));
    if (!best || score > best.score) best = { score, match: m };
  }
  if (!best) return null;
  const scheme = schemes.find((s) => s.code === best!.match.schemeCode);
  const option = scheme?.options.find((o) => o.planCode === best!.match.planCode && o.optionCode === best!.match.optionCode);
  return scheme && option ? { scheme, option } : null;
}
